import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const VideoMasonrySkeletonLoader = ({ theme = "dark", count = 12 }) => {
  const [columnCount, setColumnCount] = useState(3);
  const [videoWidth, setVideoWidth] = useState(244);
  const [masonryColumns, setMasonryColumns] = useState([]);
  const containerRef = useRef();

  // Calculate column count and video width (same logic as VideoMasonry)
  useEffect(() => {
    const updateLayout = () => {
      const isMobile = window.innerWidth <= 768;
      const gap = isMobile ? 8 : 12;
      if (isMobile) {
        // Responsive width calculation for mobile
        const containerWidth = window.innerWidth - 32; // padding left/right
        const calculatedWidth = Math.floor((containerWidth - gap) / 2);
        setColumnCount(2);
        setVideoWidth(Math.max(140, Math.min(244, calculatedWidth)));
      } else {
        const availableWidth = Math.min(1400, window.innerWidth);

        if (availableWidth <= 1000) {
          const columns = Math.floor((availableWidth + gap) / (244 + gap));
          setColumnCount(Math.max(2, columns));
          setVideoWidth(244);
        } else {
          let bestColumnCount = 2;
          let bestVideoWidth = 244;

          for (let columns = 2; columns <= 8; columns++) {
            const calculatedVideoWidth =
              (availableWidth - (columns - 1) * gap) / columns;
            if (calculatedVideoWidth >= 244) {
              bestColumnCount = columns;
              bestVideoWidth = calculatedVideoWidth;
            }
          }

          setColumnCount(bestColumnCount);
          setVideoWidth(Math.floor(bestVideoWidth));
        }
      }
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, []);

  // Create masonry layout (same logic as VideoMasonry)
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const spacing = isMobile ? 8 : 12;
    const columns = Array.from({ length: columnCount }, () => []);
    const columnHeights = Array(columnCount).fill(0);

    // Generate mock items for skeleton
    const mockItems = Array.from({ length: count }, (_, i) => ({
      _id: `skeleton-${i}`,
      id: i,
    }));

    mockItems.forEach((item, index) => {
      // Simulate various aspect ratios for skeleton items to match real content
      const aspectRatios = [
        1 / 1, // Square
        12 / 9, // Landscape
        4 / 3, // Standard
        4 / 3, // Standard
        4 / 3, // Standard
        1 / 1, // Square
        1 / 1, // Square
        1 / 1, // Square
      ];

      const aspectRatio = aspectRatios[index % aspectRatios.length];
      const height = Math.round(videoWidth * aspectRatio);

      const shortestColumnIndex = columnHeights.indexOf(
        Math.min(...columnHeights)
      );

      columns[shortestColumnIndex].push({
        ...item,
        height: height,
        width: videoWidth,
        aspectRatio: aspectRatio,
      });

      columnHeights[shortestColumnIndex] += height + spacing;
    });

    setMasonryColumns(columns);
  }, [count, columnCount, videoWidth]);

  return (
    <MasonryContainer ref={containerRef}>
      <MasonryGrid>
        {masonryColumns.map((column, columnIndex) => (
          <MasonryColumn key={columnIndex}>
            {column.map((item) => (
              <MasonryItem key={`skeleton-${item.id}`}>
                <SkeletonItem
                  theme={theme}
                  style={{
                    height: item.height,
                  }}
                >
                  {/* Thumbnail placeholder */}
                  <SkeletonThumbnail theme={theme} />

                  {/* Small rotated thumbnail */}
                  <SkeletonSmallThumb theme={theme} />

                  {/* Footer gradient overlay */}
                  <SkeletonFooter>
                    {/* Badges */}
                    <SkeletonBadges>
                      <SkeletonBadge theme={theme} width="60px" />
                    </SkeletonBadges>

                    {/* Model name */}
                    <SkeletonModelName theme={theme} />

                    {/* Username */}
                    <SkeletonUsername theme={theme} />
                  </SkeletonFooter>
                </SkeletonItem>
              </MasonryItem>
            ))}
          </MasonryColumn>
        ))}
      </MasonryGrid>
    </MasonryContainer>
  );
};

export default VideoMasonrySkeletonLoader;

const MasonryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding-bottom: 16px;
`;

const MasonryGrid = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 4px;
  width: 100%;
  padding: 0;

  @media (max-width: 768px) {
    justify-content: center;
    max-width: 100%;
    margin: 0 auto;
  }
`;

const MasonryColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const MasonryItem = styled.div`
  width: 100%;
`;

const SkeletonItem = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const SkeletonThumbnail = styled.div`
  width: 100%;
  height: 100%;
  background: ${(props) =>
    props.theme === "light"
      ? "linear-gradient(90deg, #e0e0e0 0px, #f0f0f0 40px, #e0e0e0 80px)"
      : "linear-gradient(90deg, #1a1a1a 0px, #2a2a2a 40px, #1a1a1a 80px)"};
  background-size: 468px 100%;
  animation: ${shimmer} 1.5s infinite linear, ${pulse} 2s infinite ease-in-out;
`;

const SkeletonSmallThumb = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 25;
  border-radius: 4px;
  width: 34px;
  height: 42px;
  transform: rotate(-6.016deg);
  background: ${(props) =>
    props.theme === "light"
      ? "linear-gradient(90deg, #d0d0d0 0px, #e0e0e0 40px, #d0d0d0 80px)"
      : "linear-gradient(90deg, #252525 0px, #353535 40px, #252525 80px)"};
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite linear;
`;

const SkeletonFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 61.05%,
    rgba(0, 0, 0, 0.64) 100%
  );
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const SkeletonBadges = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
`;

const SkeletonBadge = styled.div`
  height: 20px;
  width: ${(props) => props.width || "60px"};
  border-radius: 100px;
  background: ${(props) =>
    props.theme === "light"
      ? "linear-gradient(90deg, rgba(255,255,255,0.3) 0px, rgba(255,255,255,0.5) 40px, rgba(255,255,255,0.3) 80px)"
      : "linear-gradient(90deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.2) 40px, rgba(255,255,255,0.1) 80px)"};
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite linear;
`;

const SkeletonModelName = styled.div`
  height: 14px;
  width: 60%;
  max-width: 140px;
  border-radius: 4px;
  margin-bottom: 4px;
  background: ${(props) =>
    props.theme === "light"
      ? "linear-gradient(90deg, rgba(255,255,255,0.4) 0px, rgba(255,255,255,0.6) 40px, rgba(255,255,255,0.4) 80px)"
      : "linear-gradient(90deg, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.25) 40px, rgba(255,255,255,0.15) 80px)"};
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite linear;
`;

const SkeletonUsername = styled.div`
  height: 10px;
  width: 40%;
  max-width: 80px;
  border-radius: 4px;
  background: ${(props) =>
    props.theme === "light"
      ? "linear-gradient(90deg, rgba(255,255,255,0.3) 0px, rgba(255,255,255,0.5) 40px, rgba(255,255,255,0.3) 80px)"
      : "linear-gradient(90deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.2) 40px, rgba(255,255,255,0.1) 80px)"};
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite linear;
`;
